import { Globe, ImageSquare, Sparkle, type Icon as PhosphorIcon } from '@phosphor-icons/react';
import type { ProjectDomain, ProjectResponse } from '@mybusinessmate/schema';
import type { TagTone } from '@mybusinessmate/ui-tokens';

export const DOMAIN_META: Record<ProjectDomain, { icon: PhosphorIcon; tone: TagTone; label: string }> = {
  Website: { icon: Globe, tone: 'blue', label: 'Website' },
  Marketing: { icon: ImageSquare, tone: 'pink', label: 'Marketing' },
  Automation: { icon: Sparkle, tone: 'indigo', label: 'Automation' },
};

const STATUS: Record<string, { label: string; tone: TagTone }> = {
  Pending: { label: 'Queued', tone: 'grey' },
  'In Progress': { label: 'In progress', tone: 'indigo' },
  'On Hold': { label: 'On hold', tone: 'amber' },
  Completed: { label: 'Done', tone: 'green' },
  Cancelled: { label: 'Archived', tone: 'grey' },
};

/** The raw project status as the label and tone the UI shows. Unknown values show as they are. */
export const projectStatusDisplay = (status: string) => STATUS[status] ?? { label: status, tone: 'grey' as TagTone };

const dayMonth = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const dayMonthYear = new Intl.DateTimeFormat('en-IN', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

/** Dates are stored as UTC midnight (calendar dates), so they are formatted in UTC to never shift a day. */
/** ICU spells September "Sept"; the design (and everyone's calendar) writes "Sep". */
export const shortMonth = (text: string) => text.replace(/\bSept\b/, 'Sep');
export const formatDay = (iso: string) => shortMonth(dayMonth.format(new Date(iso)));
export const formatDayYear = (iso: string) => shortMonth(dayMonthYear.format(new Date(iso)));

/** "Ends 31 Oct", "Starts 6 Oct" (not started yet) or "No date yet" (Figma C03.1 date chip). */
export function projectDateLabel(project: Pick<ProjectResponse, 'startDate' | 'targetEndDate'>, now = new Date()) {
  if (project.targetEndDate) return `Ends ${formatDay(project.targetEndDate)}`;
  if (new Date(project.startDate) > now) return `Starts ${formatDay(project.startDate)}`;
  return 'No date yet';
}

/** "5 of 12 tasks done"; a project with no tasks yet says so instead of "0 of 0". */
export function taskSummary(project: Pick<ProjectResponse, 'taskCount' | 'doneTaskCount'>) {
  if (project.taskCount === 0) return 'No tasks yet';
  return `${project.doneTaskCount} of ${project.taskCount} tasks done`;
}

/** Tasks still open, for the staff list ("6 tasks · 2 open"). */
export function openTaskSummary(project: Pick<ProjectResponse, 'taskCount' | 'doneTaskCount'>) {
  if (project.taskCount === 0) return 'No tasks yet';
  const noun = project.taskCount === 1 ? 'task' : 'tasks';
  return `${project.taskCount} ${noun} · ${project.taskCount - project.doneTaskCount} open`;
}
